// Npm
import { Accordion, AccordionItem, Button, Card, Col, Row } from '@nt/dds-react';
import moment from 'moment';
import React, { FC, Fragment, ReactElement } from 'react';
import { useHistory } from 'react-router-dom';

// Local
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { selectUserDetails, setAnalyticsData } from '../../pages/Home/HomeSlice';
import { eventTriggerAnalytics, uniqueID } from '../../utils/helper';
import { FormatMessage } from '../../utils/types';
import { formatMessageHOC } from '../Hoc/formatMessageHOC';
import ItemsInProgress from './ItemsInProgress';
import './Dashboard.css';

interface Props extends FormatMessage { }

/**
 * @param {*} {f: FormatMessage}
 * @return {ReactElement}
 */
const Dashboard: FC<Props> = ({ f }): ReactElement => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { personaId, sponsorCode } = useAppSelector(selectUserDetails);

  const getFormattedContent = (text: string) => {
    return text.split('\n').map(line => (
      <Fragment key={`faq-content-item-${uniqueID()}`}>
        {line}
        <br />
      </Fragment>
    ));
  };

  const accountOpening = () => {
    const draftRandomId = uniqueID();
    eventTriggerAnalytics({
      category: 'Account Open',
      eventTrack: 'gaTrackEvent_open_account',
      eventTitle: 'Open an Account',
      pageObject: {
        pageName: 'Dashboard',
        pageRoute: '/home'
      },
      personaId: personaId as number,
      sponsorCode: sponsorCode as string,
      draftRandomId
    });
    dispatch(
      setAnalyticsData({ accountOpenTimestamp: moment().format('YYYY-MM-DD HH:mm:ss'), draftRandomId: draftRandomId })
    );
    history.push('/legalentity/entitylist');
  };

  return (
    <div className="home-container">
      <Row>
        <Col md={8} className="home-heading-wrap">
          <h2 className="font-xxl fontweight-500">{f({ id: 'home.openingHeader' })}</h2>
        </Col>
        <Col md={4} className="home-button-wrap">
          <Button
            danger={undefined}
            icon="none"
            iconName=""
            kind="primary"
            size="small"
            type="button"
            onClick={accountOpening}
          >
            {f({ id: 'home.openAccount' })}
          </Button>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <ItemsInProgress />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card hover={false} noPadding={true} shadowSize="xs" className="faq-wrap">
            <div className="home-card-header-wrap">
              <h2 className="roboto-font card-heading">{f({ id: 'dashboard.faq.heading' })}</h2>
              <Button
                danger={undefined}
                icon="none"
                iconName="settings"
                kind="borderless"
                onClick={() => undefined}
                size="small"
                type="button"
              >
                {f({ id: 'dashboard.faq.button' })}
              </Button>
            </div>
            <div className="card-accordion-wrap">
              <Accordion>
                <AccordionItem iconPosition="left" title={f({ id: 'dashboard.faq.title1' })} uuid="item-1">
                  <div className="padding-lg">{getFormattedContent(f({ id: 'dashboard.faq.answer1' }))}</div>
                </AccordionItem>
                <AccordionItem iconPosition="left" title={f({ id: 'dashboard.faq.title2' })} uuid="item-2">
                  <div className="padding-lg">{getFormattedContent(f({ id: 'dashboard.faq.answer2' }))}</div>
                </AccordionItem>
                <AccordionItem iconPosition="left" title={f({ id: 'dashboard.faq.title3' })} uuid="item-3">
                  <div className="padding-lg">{getFormattedContent(f({ id: 'dashboard.faq.answer3' }))}</div>
                </AccordionItem>
                <AccordionItem iconPosition="left" title={f({ id: 'dashboard.faq.title4' })} uuid="item-4">
                  <div className="padding-lg">{getFormattedContent(f({ id: 'dashboard.faq.answer4' }))}</div>
                </AccordionItem>
                <AccordionItem iconPosition="left" title={f({ id: 'dashboard.faq.title5' })} uuid="item-5">
                  <div className="padding-lg">{getFormattedContent(f({ id: 'dashboard.faq.answer5' }))}</div>
                </AccordionItem>
                <AccordionItem iconPosition="left" title={f({ id: 'dashboard.faq.title6' })} uuid="item-6">
                  <div className="padding-lg">{getFormattedContent(f({ id: 'dashboard.faq.answer6' }))}</div>
                </AccordionItem>
              </Accordion>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <div className="need-help-cards-wrap">
            <Card hover={false} noPadding={false} shadowSize="xs" className="need-help-wrap">
              <h2 className="roboto-font need-help-heading">{f({ id: 'home.helpText' })}</h2>
              <p className="roboto-font need-help-description">
                {f({ id: 'home.helpDescription' })}
                <a href={`mailto:${f({ id: 'home.helpDescription.email' })}`} className="need-help-mail">
                  {f({ id: 'home.helpDescription.email' })}
                </a>
              </p>
            </Card>
            <Card hover={false} noPadding={false} shadowSize="xs" className="need-help-wrap">
              <h2 className="roboto-font need-help-heading">{f({ id: 'home.surveyText' })}</h2>
              <p className="roboto-font need-help-description">
                {f({ id: 'home.surveyDescription' })}
                <br />
                <a href={f({ id: 'home.surveyDescription.linkURL' })} target='_blank' rel='noopener noreferrer' className="need-help-mail">
                  {f({ id: 'home.surveyDescription.link' })}
                </a>
              </p>
            </Card>
          </div>
        </Col>
      </Row>
    </div>
  );
};

Dashboard.displayName = 'Dashboard';
export default formatMessageHOC(Dashboard);
